import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { VersionStatistics } from '../../types';
import { formatNumber } from '../../helpers';

const PURPLE = '#7c3aed';
const BLUE = '#2563eb';
const TEAL = '#0e7490';

function compact(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K`;
  return n.toLocaleString();
}

function HeroCard({
  total,
  label,
  accentColor,
  leftCount,
  leftLabel,
  rightCount,
  rightLabel,
}: {
  total: number | undefined;
  label: string;
  accentColor: string;
  leftCount: number | undefined;
  leftLabel: string;
  rightCount: number | undefined;
  rightLabel: string;
}) {
  return (
    <div
      className="flex-1 rounded-xl border border-gray-100 bg-white p-6 flex flex-col gap-4"
      style={{ borderTop: `3px solid ${accentColor}` }}
    >
      <div>
        <p className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: accentColor }}>
          {label}
        </p>
        <p className="text-4xl font-extrabold tabular-nums mt-1 text-gray-800">{compact(total)}</p>
        <p className="text-xs text-gray-400 mt-0.5">total associations</p>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-50 mt-auto">
        <div className="flex-1 text-center">
          <p className="text-xl font-bold tabular-nums text-gray-700">{compact(leftCount)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{leftLabel}</p>
        </div>
        <span className="text-gray-200 text-base select-none">↔</span>
        <div className="flex-1 text-center">
          <p className="text-xl font-bold tabular-nums text-gray-700">{compact(rightCount)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{rightLabel}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Small key metric card ────────────────────────────────────────────────────
function MetricCard({ value, label, color }: { value: number | undefined; label: string; color: string }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-4">
      <p className="text-2xl font-bold tabular-nums" style={{ color }}>
        {value !== undefined ? compact(value) : '—'}
      </p>
      <p className="text-[12px] text-gray-400 mt-1 leading-snug">{label}</p>
    </div>
  );
}

// ─── Row inside the additional stats accordion ────────────────────────────────
function StatRow({ label, value }: { label: string; value: number | undefined }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-gray-700">{formatNumber(value)}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const StatisticsDiagram = ({ statistics: s }: { statistics: VersionStatistics }) => {
  const [expanded, setExpanded] = useState(false);

  const keyMetrics = [
    { value: s?.gene, label: 'Genes', color: PURPLE },
    { value: s?.disease ?? s?.gda_disease, label: 'Diseases & Phenotypes', color: PURPLE },
    { value: s?.vda_variant, label: 'Variants', color: BLUE },
    { value: s?.publication, label: 'Publications', color: TEAL },
    { value: s?.dda, label: 'Disease-Disease Associations', color: TEAL },
    { value: s?.chemical ?? s?.chemicals_gda_or_vda, label: 'Chemicals & Drugs', color: TEAL },
  ];

  const additionalStats = [
    { label: 'GDA Evidences', value: s?.gda_evidence },
    { label: 'GDA Publications', value: s?.gda_publication },
    { label: 'Animal Model Assoc.', value: s?.gda_animal_model },
    { label: 'Animal Model Diseases', value: s?.gda_animal_model_disease },
    { label: 'GDAs with Chemicals', value: s?.gda_chemical },
    { label: 'Chemicals linked to GDAs', value: s?.chemical_gda },
    { label: 'Genes linked to Chemicals', value: s?.gda_chemical_gene },
    { label: 'Diseases linked to Chemicals (GDA)', value: s?.gda_chemical_disease },
    { label: 'VDA Evidences', value: s?.vda_evidence },
    { label: 'VDA Publications', value: s?.vda_publications ?? s?.vda_publication },
    { label: 'VDAs with Chemicals', value: s?.vda_chemical },
    { label: 'Chemicals linked to VDAs', value: s?.chemical_vda },
    { label: 'Variants linked to Chemicals', value: s?.vda_chemical_variant },
    { label: 'Diseases linked to Chemicals (VDA)', value: s?.vda_chemical_disease },
    { label: 'Genotype-Phenotype Assoc.', value: s?.gda_or_vda },
    { value: s?.evidence, label: 'Evidence Pieces', color: TEAL },
    ...(s?.genes_in_gda_vda ? [{ label: 'Genes (incl. variant-related)', value: s.genes_in_gda_vda }] : []),
  ].filter(r => r.value !== undefined);

  return (
    <div className="space-y-4">
      {/* ── Hero cards ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-4">
        <HeroCard
          total={s?.gda_summary}
          label="Gene–Disease Associations"
          accentColor={PURPLE}
          leftCount={s?.gene}
          leftLabel="Genes"
          rightCount={s?.disease ?? s?.gda_disease}
          rightLabel="Diseases"
        />
        <HeroCard
          total={s?.vda_summary}
          label="Variant–Disease Associations"
          accentColor={BLUE}
          leftCount={s?.vda_variant}
          leftLabel="Variants"
          rightCount={s?.vda_disease}
          rightLabel="Diseases"
        />
      </div>

      {/* ── Key metrics grid ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {keyMetrics.map(m => (
          <MetricCard key={m.label} value={m.value} label={m.label} color={m.color} />
        ))}
      </div>

      {/* ── Additional statistics (collapsible) ─────────────────────────── */}
      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setExpanded(e => !e)}
        >
          <span className="text-sm font-semibold text-gray-500">Additional Statistics</span>
          <ChevronDownIcon
            className="w-4 h-4 text-gray-400 transition-transform duration-200"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>
        {expanded && (
          <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            {additionalStats.map(r => (
              <StatRow key={r.label} label={r.label} value={r.value} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsDiagram;
